type Theme = 'light' | 'dark';
interface Cursor { slide: number; step: number }
interface ExportedPlayer {
  getCursor(): Cursor;
  goTo(cursor: Cursor): void;
  goToSlide(index: number): void;
}

/** Site adapter for the unmodified Deckwerk standalone player. */
export function initPaperDeckPlayer(): void {
  const exported = window as Window & {
    __SLIDE_PLAYER__?: ExportedPlayer;
    __DECK__?: { slides: unknown[] };
  };
  const player = exported.__SLIDE_PLAYER__;
  const total = exported.__DECK__?.slides.length ?? 0;
  if (!player || !total) return;

  const applyTheme = (theme: Theme): void => {
    document.documentElement.dataset['theme'] = theme;
    document.documentElement.style.colorScheme = theme;
  };
  let saved: string | null = null;
  try {
    saved = sessionStorage.getItem('theme');
  } catch {
    /* Standalone files and private browsing may not permit storage. */
  }
  const preference = window.matchMedia('(prefers-color-scheme: dark)');
  applyTheme(saved === 'light' || saved === 'dark' ? saved : preference.matches ? 'dark' : 'light');
  preference.addEventListener('change', (event) => {
    if (window.parent === window && saved !== 'light' && saved !== 'dark') {
      applyTheme(event.matches ? 'dark' : 'light');
    }
  });

  const report = (): void => {
    const slide = player.getCursor().slide + 1;
    window.history.replaceState(null, '', `#${slide}`);
    if (window.parent !== window) {
      window.parent.postMessage({ source: 'paper-deck', type: 'state', slide, total }, window.location.origin);
    }
  };
  // All slide changes (keys, clicks, deep links, and host buttons) use goTo.
  const goTo = player.goTo.bind(player);
  player.goTo = (cursor) => {
    goTo(cursor);
    report();
  };

  window.addEventListener('message', (event: MessageEvent<unknown>) => {
    if (event.source !== window.parent || event.origin !== window.location.origin) return;
    if (!event.data || typeof event.data !== 'object') return;
    const message = event.data as Record<string, unknown>;
    if (message['source'] !== 'paper-deck-host') return;
    if (message['type'] === 'init') {
      if (message['theme'] === 'dark' || message['theme'] === 'light') applyTheme(message['theme']);
      report();
    } else if (message['type'] === 'navigate') {
      const slide = message['slide'];
      if (typeof slide === 'number' && Number.isInteger(slide) && slide >= 1 && slide <= total) {
        player.goToSlide(slide - 1);
      }
    }
  });
  report();
}
